import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, GraduationCap } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import useAuthStore from "../store/authStore";
import { useState } from "react";

const navLinks = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "For Business", href: "/b2b" },
  { label: "Moving Bundles", href: "#bundles" },
  { label: "Price Calculator", href: "#calculator" },
];

// Highlighted nav entry — rendered with its own style (pill + icon) to draw
// the eye to the new college vertical. Separate from the plain navLinks array
// so the pill styling stays crisp and it's always visible on mobile too.
const highlightedLink = {
  label: "College Logistics",
  href: "/college",
  icon: GraduationCap,
  tag: "NEW",
};

export default function Navbar() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavClick = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
      setMobileOpen(false);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  return (
    <nav className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50 flex items-center justify-between px-6" data-testid="landing-navbar">
      <Link to="/" className="flex items-center gap-2.5 no-underline" data-testid="navbar-logo">
        <Logo size="sm" />
      </Link>

      {/* Desktop nav links */}
      <div className="hidden lg:flex items-center gap-1">
        {navLinks.map((link) => (
          <button
            key={link.label}
            onClick={() => handleNavClick(link.href)}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-50 transition-all font-medium"
            data-testid={`navbar-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {link.label}
          </button>
        ))}
        {/* Highlighted College Logistics CTA — orange pill with NEW badge */}
        <button
          onClick={() => handleNavClick(highlightedLink.href)}
          className="ml-1 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-r from-[#FF6B00] to-[#FF8533] hover:from-[#E65A00] hover:to-[#FF6B00] px-3.5 py-2 rounded-full shadow-sm shadow-[#FF6B00]/25 transition-all hover:scale-105"
          data-testid="navbar-link-college-logistics"
        >
          <highlightedLink.icon className="h-4 w-4" />
          {highlightedLink.label}
          <span className="text-[9px] font-bold tracking-wider bg-white/25 px-1.5 py-0.5 rounded-full">{highlightedLink.tag}</span>
        </button>
      </div>

      {/* Desktop CTA */}
      <div className="hidden md:flex items-center gap-3">
        {user ? (
          <Button
            onClick={() => navigate("/dashboard")}
            className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-full px-6 py-2.5 font-medium shadow-sm shadow-[#FF6B00]/20 transition-all"
            data-testid="navbar-dashboard-btn"
          >
            Dashboard
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl px-5 py-2.5 font-medium transition-all"
              data-testid="navbar-login-btn"
            >
              Log in
            </Button>
            <Button
              onClick={() => navigate("/dashboard/orders")}
              className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-full px-6 py-2.5 font-medium shadow-sm shadow-[#FF6B00]/20 transition-all"
              data-testid="navbar-book-pickup-btn"
            >
              Book Pickup
            </Button>
          </>
        )}
      </div>

      {/* Mobile hamburger */}
      <button
        className="md:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="navbar-mobile-toggle"
      >
        {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="absolute top-16 left-0 right-0 bg-white border-b border-slate-200 p-4 flex flex-col gap-1 md:hidden shadow-lg z-50" data-testid="navbar-mobile-menu">
          {/* Highlighted College Logistics — mobile pill, sits at the top */}
          <button
            onClick={() => handleNavClick(highlightedLink.href)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-[#FF6B00] to-[#FF8533] px-3.5 py-2.5 rounded-full mb-1 shadow-sm shadow-[#FF6B00]/25"
            data-testid="navbar-mobile-link-college-logistics"
          >
            <highlightedLink.icon className="h-4 w-4" />
            {highlightedLink.label}
            <span className="ml-auto text-[9px] font-bold tracking-wider bg-white/25 px-1.5 py-0.5 rounded-full">{highlightedLink.tag}</span>
          </button>
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href)}
              className="text-left text-sm text-slate-600 hover:text-slate-900 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-all font-medium"
            >
              {link.label}
            </button>
          ))}
          <div className="border-t border-slate-100 mt-2 pt-2 flex flex-col gap-2">
            {user ? (
              <Button
                onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}
                className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl w-full"
                data-testid="navbar-mobile-dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => { navigate("/login"); setMobileOpen(false); }}
                  className="w-full text-slate-600 hover:bg-slate-50 rounded-xl"
                  data-testid="navbar-mobile-login-btn"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => { navigate("/dashboard/orders"); setMobileOpen(false); }}
                  className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl w-full"
                  data-testid="navbar-mobile-book-pickup-btn"
                >
                  Book Pickup
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
